import { User, Dataset, SearchableTypeName } from "src/lib/graphql/types";

declare global {
  interface Window {
    // Google Tag Manager (script added in <Head /> if not blocked)
    // eslint-disable-next-line no-unused-vars
    dataLayer?: { push: (payload: any) => void };
  }
}

export interface DBItem {
  id: string;
  updatedAt: string;
  createdAt: string;
}

export const BehaviorLanguages = ["JavaScript", "Rust", "Python"] as const;

type BehaviorLanguage = typeof BehaviorLanguages[number];

export interface IScrapedDataset extends DBItem {
  categoryInSource?: string[];
  contactPoint?: string;
  contactPointEmail?: string;
  dateModified?: Date;
  datePublished?: Date;
  description?: string;
  format?: string;
  handled?: boolean;
  ignored?: boolean;
  includedInDataCatalog?: string;
  isPartOf?: string;
  issn?: string;
  keywords?: string[];
  keywordsInSource: string[];
  landingPage?: string;
  language?: string;
  license?: string;
  licenseInSource: string;
  licenseUrlInSource?: string;
  measurementTechnique?: string;
  organizationInSource?: string;
  organizationLogoInSource?: string;
  published?: boolean;
  publisher?: string;
  publisherInSource: string;
  references?: string[];
  rights?: string;
  scraped?: boolean;
  shortname?: string;
  source?: string;
  sourceId?: string;
  sourceUrl?: string;
  spatialCoverage?: string;
  subject?: string[];
  subjectInSource: string;
  temporalCoverage?: string;
  temporalFrequency?: string;
  title?: string;
  updateFreq?: string;
  updated?: boolean;
  variableMeasured?: string;
  version?: string;
  resources: Array<{
    id: string;
    name?: string;
    shortname?: string;
    revisionId?: string;
    format?: string;
    preview?: string[][];
    description?: string;
    describedBy?: string;
    describedByType?: string;
    url?: string;
    size?: string | number;
    createdAt?: string;
    updatedAt?: string;
  }>;
}

export type Resource = IScrapedDataset["resources"][0];

export interface IRole extends DBItem {
  name: string;
  description: string;
  title?: string;
}

export interface IOrg extends DBItem {
  name: string;
  alternateName: string;
  description?: string;
  address?: IPostalAddress;
  location?: string;
  legalName?: string;
  shortname: string;
  roles: IRole[];
  logo?: string;
  avatar?: string;
  members: User[];
  memberCount: number;
  image?: string[];
  url?: string[];
  supportContact?: string;
  publicMembership?: boolean;
  parentOrganization?: {
    name: string;
    id: string;
    shortname: string;
  };
  subOrganization: [
    {
      id: string;
      name: string;
      shortname: string;
      logo?: string;
    }
  ];
}

export type SearchType =
  | "models"
  | "data"
  | "behaviors"
  | "schemas"
  | "all"
  | "search"
  | SearchableTypeName
  | null;

export type IndexSearchQuery = {
  perPage?: string | number | null;
  page?: string | number | null;
  subject?: string | string[] | null;
  language?: BehaviorLanguage | null;
  spatialCoverage?: string | null;
  temporalCoverage?: string | null;
  dataFrequency?: string | null;
  query?: string | null;
  contentType?: IndexListingType | null;
  searchType?: SearchType;
  sort?: SortOption;
  releasedOnly?: string | null;
};

export type IndexSearchParam = keyof IndexSearchQuery;

export type SortOption = "relevance" | "popularity" | "updatedAt" | "createdAt";

export interface ILicense extends DBItem {
  name: string;
  description?: string;
  logo?: string;
  url?: string;
  default: boolean;
  nonCore?: boolean;
}

export interface IKeyword {
  name: string;
  count: number;
}

export const IndexListingTypes = ["Simulation", "Dataset", "Behavior"] as const;

export type IndexListingType = typeof IndexListingTypes[number];

export interface IPostalAddress {
  postOfficeBoxNumber?: string;
  streetAddress?: string;
  addressLocality?: string;
  addressRegion?: string;
  addressCountry?: string;
  postalCode?: string;
}

export interface IOrgInfo {
  org: IOrg;
  role: IRole;
  jobTitle: string;
}

export type FormSubmission = {
  [key: string]: string;
};

export const isUser = <T extends User = User>(entity?: any): entity is T => {
  if (!entity) {
    return false;
  }
  return entity.__typename ? entity.__typename === "User" : !!entity.fullName;
};

export const isDataset = (file: any): file is Dataset => {
  return file.__typename === "Dataset";
};

export type ReactSelectOption = {
  label: string | JSX.Element;
  value: string;
  nonCore?: boolean;
};

export type SortSelectOption = {
  label: string;
  value: SortOption;
};

export type JobListing = {
  title: string;
  category: string;
  locationLabel?: string;
  startingSection?: { content: string }[];
  requirements?: string[];
  closingSection?: { content: string }[];
  formURL: string;
  slug: string;
};

export type MailchimpUser = {
  id: string;
  email: string;
  merge_fields?: {
    FNAME?: string;
    LNAME?: string;
    ACTIVATED?: "Yes" | null;
  };
};
